import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styles from "./style.scss";
import DebounceSelect from "./DebounceSelect/DebounceSelect";
import { selectCurrentModule } from "../../../containers/App/selectors";
import { useAnimatedLocationPlaceholder } from "./useAnimatedLocationPlaceholder";
import { api } from "../../../services";
import { LocationLevel, ZagrebCountyId } from "../../../models/enums";
import { LocationSearchResultI } from "../../../typings/configurationTypes";

// Below this length the API returns nothing; we don't even ask.
const MIN_INPUT_LENGTH = 2;
const filterDebounceMs = 200;
let getSuggestionTimeout;

// Location filter of the search page: a type-ahead over Croatian counties,
// cities and settlements served by configuration/locations/search.
const SearchInputLocation = props => {
  const { child, showPlaceholder, value, module, getPopupContainer, title } = props;

  const hasValue = value && Object.keys(value).some(k => Array.isArray(value[k]) && value[k].length > 0);
  // Pause the placeholder animation while the dropdown/modal is open; its
  // ~40-80ms tick re-renders the Select and breaks the dropdown's inner scroll.
  const [isActive, setIsActive] = useState(false);
  const animatedPlaceholder = useAnimatedLocationPlaceholder(!hasValue && !isActive);

  const [foundLocations, setFoundLocations] = useState([]);
  // True from the moment the user starts typing until the search response for
  // the latest query has arrived. Threaded down to DebounceSelect so its
  // `notFoundContent` shows a spinner instead of "Nema željene lokacije"
  // while we're still waiting on the API.
  const [isFiltering, setIsFiltering] = useState(false);
  // Latest query kept in a ref so a slow response for an older query can't
  // overwrite the results of a newer one.
  const currentQueryRef = useRef("");
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearTimeout(getSuggestionTimeout);
    };
  }, []);

  const runSearch = async (query: string) => {
    if (!query || query.trim().length < MIN_INPUT_LENGTH) {
      setFoundLocations([]);
      setIsFiltering(false);
      return;
    }

    try {
      const results: LocationSearchResultI[] = await api.Configuration.searchLocations(query.trim());

      if (!isMounted.current || currentQueryRef.current !== query) {
        return;
      }

      setFoundLocations(results || []);
    } catch (error) {
      if (!isMounted.current || currentQueryRef.current !== query) {
        return;
      }

      setFoundLocations([]);
    }

    setIsFiltering(false);
  };

  // Defer the request off the keystroke event so fast typing sends one
  // request per pause instead of one per character; the dropdown's
  // defaultActiveFirstOption={false} prevents the late setState from
  // snapping the scroll back to the top.
  const getFoundedLocations = e => {
    const { onChange } = props;
    const value = e;
    currentQueryRef.current = value;

    if (value == "") {
      onChange(undefined);
      setIsFiltering(false);
      setFoundLocations([]);
      return;
    }

    if (value.trim().length < MIN_INPUT_LENGTH) {
      clearTimeout(getSuggestionTimeout);
      setIsFiltering(false);
      setFoundLocations([]);
      return;
    }

    setIsFiltering(true);
    clearTimeout(getSuggestionTimeout);
    getSuggestionTimeout = setTimeout(() => runSearch(value), filterDebounceMs);
  };

  // Counties and cities are shown by name alone; only settlements get their
  // city as a prefix ("Grad > Naselje"), because settlement names repeat
  // across the country while city names don't. Zagreb districts sit at the
  // city level under the "Grad Zagreb" county, so they keep that prefix.
  const renderOptionText = (item: LocationSearchResultI) => {
    if (item.level === LocationLevel.settlement && item.cityName) {
      return `${item.cityName} > ${item.name}`;
    }

    if (item.level === LocationLevel.city && item.countyId === ZagrebCountyId && item.countyName) {
      return `${item.countyName} > ${item.name}`;
    }

    return item.name;
  };

  return (
    <React.Fragment>
      {showPlaceholder ? null : <span className="control-title">{title}</span>}
      <div className={styles.menuWrapper}>
        <DebounceSelect
          filterLocations={getFoundedLocations}
          foundLocations={foundLocations}
          renderOptionText={renderOptionText}
          onChange={props.onChange}
          selectedValues={value}
          placeholder={animatedPlaceholder}
          modalTitle={title || "Lokacija"}
          getPopupContainer={getPopupContainer}
          onActiveChange={setIsActive}
          fetching={isFiltering}
        />
      </div>
    </React.Fragment>
  );
};

function mapStateToProps(state) {
  return {
    module: selectCurrentModule(state),
  };
}

export default connect(mapStateToProps, {})(SearchInputLocation);



// WEBPACK FOOTER //
// ./src/components/FormControls/SearchInputLocation/index.tsx